// Class-aware aggregation. The one place that decides what a set of section
// numbers reduces to — so the "summed a rate" warning has exactly one origin.

import type { CellValue } from '@/model/types';
import { summariseNested } from './classify';
import type { DataRow, MeasureClass } from './types';

export type AggKind = 'auto' | 'sum' | 'avg' | 'min' | 'max' | 'count' | 'countUnique' | 'weighted' | 'first';

export interface AggWarning {
  kind: 'rate-summed' | 'no-weight' | 'mixed-types' | 'skipped' | 'unknown-field';
  field: string;
  message: string;
}

export interface AggResult {
  /** Usually a number. `first` and the text/nested classes can return a string. */
  value: CellValue;
  warnings: AggWarning[];
  /** How many rows actually contributed a finite number. */
  n: number;
}

/**
 * What a weighted mean weights by, in preference order.
 *
 * FLOOR area, not footprint. A rate is per square metre of the area it applies
 * to, so weighting by `area` under-weights a nine-storey tower ninefold against
 * a single-storey box on the same footprint — its $/m² counts once where it
 * should count nine times.
 *
 * It also makes a ratio come out right. Efficiency is net ÷ gross, and a mean
 * of net_i/gross_i weighted by gross_i is exactly Σnet ÷ Σgross — the number
 * anyone would compute by hand. Weighted by footprint it is a different number:
 * on the demo scheme the two read 90% and 86%.
 */
export const WEIGHT_FIELDS = ['grossArea', 'area'] as const;

/** The footprint field itself, for callers that want it explicitly. */
export const FOOTPRINT_FIELD = 'area';

/** The best weight the rows actually carry. */
export function resolveWeightField(rows: readonly DataRow[], preferred?: string): string {
  if (preferred) return preferred;
  for (const field of WEIGHT_FIELDS) {
    if (rows.some((r) => typeof r.properties[field] === 'number' && (r.properties[field] as number) > 0)) {
      return field;
    }
  }
  return FOOTPRINT_FIELD;
}

export function numbersOf(rows: readonly DataRow[], field: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const v = r.properties[field];
    if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
    else if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) out.push(n);
    }
  }
  return out;
}

/**
 * `auto` resolves through the field's measure class: plan and total sum,
 * rate takes an area-weighted mean, text counts.
 *
 * An explicit `sum` over a rate field is honoured — the product decision is
 * "warn but allow" — but it returns a warning alongside the number so the UI
 * can mark the column and the status bar can count it.
 */
export function aggregate(
  rows: readonly DataRow[],
  field: string,
  kind: AggKind,
  cls: MeasureClass,
  /** Override what a weighted mean weights by. Defaults to floor area. */
  weightBy?: string
): AggResult {
  const warnings: AggWarning[] = [];
  const resolved: Exclude<AggKind, 'auto'> =
    kind === 'auto'
      ? cls === 'rate'
        ? 'weighted'
        : cls === 'text' || cls === 'nested'
          ? // A text or nested column shows its VALUE when the group agrees on
            // one — a usages table grouped by name has one row per group, and
            // "1" there would be useless where the dwelling mix should be.
            'first'
          : 'sum'
      : kind;

  if (resolved === 'first') {
    return { value: firstShared(rows, field, cls), warnings, n: rows.length };
  }

  if (resolved === 'count') {
    return { value: rows.length, warnings, n: rows.length };
  }

  if (resolved === 'countUnique') {
    // Distinct VALUES of the field, not distinct rows — "how many usages",
    // "how many buildings", "how many unit types".
    const n = distinct(rows, field).length;
    return { value: n, warnings, n };
  }

  const values = numbersOf(rows, field);
  if (values.length === 0) return { value: null, warnings, n: 0 };

  switch (resolved) {
    case 'sum': {
      if (cls === 'rate') {
        warnings.push({
          kind: 'rate-summed',
          field,
          message: `${field} is a rate (per-unit) — summing it gives a number with no meaning. Use a weighted average, or GI.WEIGHTEDAVG("${field}").`,
        });
      }
      return { value: values.reduce((a, b) => a + b, 0), warnings, n: values.length };
    }
    case 'avg':
      return { value: values.reduce((a, b) => a + b, 0) / values.length, warnings, n: values.length };
    case 'min':
      return { value: Math.min(...values), warnings, n: values.length };
    case 'max':
      return { value: Math.max(...values), warnings, n: values.length };
    case 'weighted':
      return weightedMean(rows, field, warnings, resolveWeightField(rows, weightBy));
  }
}

function weightedMean(
  rows: readonly DataRow[],
  field: string,
  warnings: AggWarning[],
  weightField: string
): AggResult {
  let num = 0;
  let den = 0;
  let n = 0;
  for (const r of rows) {
    const v = r.properties[field];
    const w = r.properties[weightField];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    n += 1;
    if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
      num += v * w;
      den += w;
    }
  }
  if (n === 0) return { value: null, warnings, n: 0 };
  // One value IS the mean, however it would have been weighted. Warning about a
  // missing weight here would flag every row of a usage table, where each group
  // is a single usage.
  if (n === 1) return { value: num > 0 && den > 0 ? num / den : onlyNumber(rows, field), warnings, n: 1 };
  if (den === 0) {
    // No usable weights — fall back to the plain mean and say so, rather than
    // returning nothing or silently pretending the weighting happened.
    warnings.push({
      kind: 'no-weight',
      field,
      message: `${field} has no "${weightField}" to weight by — showing a plain average instead.`,
    });
    const vals = numbersOf(rows, field);
    return { value: vals.reduce((a, b) => a + b, 0) / vals.length, warnings, n: vals.length };
  }
  return { value: num / den, warnings, n };
}

/**
 * The value the whole group shares, or a count when it does not agree. Nested
 * values are summarised — the cell shows a view of the object, not the object.
 */
function firstShared(rows: readonly DataRow[], field: string, cls: MeasureClass): CellValue {
  const seen: unknown[] = [];
  for (const r of rows) {
    const v = r.properties[field];
    if (v === null || v === undefined || v === '') continue;
    if (!seen.some((s) => sameValue(s, v))) seen.push(v);
    if (seen.length > 1) break;
  }
  if (seen.length === 0) return null;
  if (seen.length > 1) return `${seen.length}+ values`;
  const only = seen[0];
  if (cls === 'nested' || (only !== null && typeof only === 'object')) return summariseNested(only);
  if (typeof only === 'number' || typeof only === 'boolean' || typeof only === 'string') return only;
  return String(only);
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function onlyNumber(rows: readonly DataRow[], field: string): number | null {
  const values = numbersOf(rows, field);
  return values.length > 0 ? values[0]! : null;
}

/** Distinct values of a field, in first-seen order, blanks dropped. */
export function distinct(rows: readonly DataRow[], field: string): (string | number)[] {
  const seen = new Set<string>();
  const out: (string | number)[] = [];
  for (const r of rows) {
    const v = r.properties[field];
    if (v == null || v === '') continue;
    const k = String(v);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(typeof v === 'number' ? v : k);
  }
  return out;
}

/** Group rows by a field's value, preserving first-seen order. */
export function groupBy(rows: readonly DataRow[], field: string | null): Map<string, DataRow[]> {
  const map = new Map<string, DataRow[]>();
  if (field === null) {
    for (const r of rows) map.set(r.key, [r]);
    return map;
  }
  for (const r of rows) {
    const v = r.properties[field];
    const k = v == null || v === '' ? '(blank)' : String(v);
    const bucket = map.get(k);
    if (bucket) bucket.push(r);
    else map.set(k, [r]);
  }
  return map;
}
